import React from "react";
import { Link } from "react-router-dom";
import { NumberField, TextField } from "Utils/renderField/fieldsHookForms";
import { RenderKilogramo } from "Utils/renderField/renderReadField";
import {
    rutaPacasAlCorte,
    rutaPacasCierreCerrado,
    rutaReajusteFila,
} from "../helpers/filaCierre";
import "../styles/CierreInventarioTable.css";
import NullableValue from "../../../../../../Utils/renderField/nullableValue";
const alertaIcon = require("assets/img/icons/alerta_roja.png");

const CeldaKg = ({ valor, className = "" }) => (
    <td className={`text-right text-nowrap ${className}`}>
        <RenderKilogramo value={valor} decimalScale={2} />
    </td>
);

// Aviso de que la fila tiene reajustes que quedaron desactualizados porque la
// existencia cambió después de registrarlos
const AvisoObsoletos = ({ cantidad }) => {
    if (!cantidad) return null;

    return (
        <img
            src={alertaIcon}
            alt="Reajustes desactualizados"
            title={
                `${cantidad} reajuste(s) de esta fila quedaron ` +
                "desactualizados. Revíselos antes de cerrar."
            }
            className="cierre-inventario__aviso mb-1"
        />
    );
};

// Los kg llegan como string desde el backend y pueden venir vacíos, por eso se
// valida la ausencia del dato y no su valor
const aNumero = (valor) => {
    if (valor === "" || valor === null || valor === undefined) return 0;

    return Number(valor);
};

const totalColumna = (filas, campo) =>
    filas.reduce((total, fila) => total + aNumero(fila[campo]), 0);

// negativa cuando falta producto contra lo que el sistema esperaba
const diferenciaKg = (fila) =>
    aNumero(fila.kg_fisico) - aNumero(fila.kg_teorico);

// La fila tiene reajuste cuando el físico dejó de coincidir con el teórico, y
// también cuando volvieron a coincidir pero alguna línea quedó desactualizada:
// en ese caso igual hay que revisarla antes de cerrar
const tieneReajuste = (fila) =>
    diferenciaKg(fila) !== 0 || !!fila.reajustes_obsoletos;

// Pantalla de pallets de una fila de pacas: la de reajuste cuando el corte los
// admite, y la de solo lectura en un cierre cerrado o en un corte anterior al
// último movimiento de bodega
const enlacePallets = (fila, campo, hook, cierreId) => {
    if (campo !== "pacas") return null;

    if (cierreId) {
        return {
            ruta: rutaPacasCierreCerrado(cierreId, fila),
            titulo: "Ver pacas por pallet",
        };
    }

    if (!hook) return null;

    if (hook.existenciasAlCorte === true) {
        return {
            ruta: rutaReajusteFila(campo, fila),
            titulo: "Reajustar por pallet",
        };
    }

    if (hook.existenciasAlCorte === false && hook.fechaCorte) {
        return {
            ruta: rutaPacasAlCorte(hook.fechaCorte, fila),
            titulo: "Ver pacas por pallet al corte",
        };
    }

    return null;
};

// Inventario físico de la fila. Nunca se digita como total: sale de la
// existencia real de bodega y solo cambia registrando un reajuste, por eso la
// celda se resalta cuando la fila ya tiene alguno
const CeldaInventarioFisico = ({ fila, index, campo, hook, cierreId }) => {
    const kgFisico = (
        <RenderKilogramo value={fila.kg_fisico} decimalScale={2} />
    );

    // Si bodega tuvo movimientos después del corte, su existencia ya los
    // incluye, así que reajustarla ahora la desincronizaría del período que
    // abarca el cierre
    const reajustable = !!hook && hook.existenciasAlCorte === true;

    // el sobrante es un solo saldo en kg, así que se captura en esta misma
    // tabla; las pacas se reajustan pallet por pallet en su propia pantalla
    const esSobranteEditable = reajustable && campo === "sobrante";

    const contenido = () => {
        if (esSobranteEditable) {
            return (
                <NumberField
                    control={hook.formControl}
                    errors={hook.formErrors}
                    name={`${campo}.${index}.kg_fisico`}
                    decimalScale={2}
                    className="text-right"
                    onBlur={() => hook.GuardarReajusteSobrante(fila, index)}
                />
            );
        }

        const enlace = enlacePallets(fila, campo, hook, cierreId);

        if (enlace) {
            return (
                <Link
                    to={enlace.ruta}
                    className="btn-link-pointer"
                    title={enlace.titulo}
                >
                    {kgFisico}
                </Link>
            );
        }

        return kgFisico;
    };

    const claseAncho = esSobranteEditable
        ? "cierre-inventario__celda-editable"
        : "text-right text-nowrap";

    const claseReajuste = tieneReajuste(fila)
        ? "cierre-inventario__celda-reajustada"
        : "";

    return (
        <td className={`${claseAncho} ${claseReajuste}`}>
            <div className="d-flex gap-2 justify-content-end align-items-center">
                <AvisoObsoletos cantidad={fila.reajustes_obsoletos} />
                {contenido()}
            </div>
        </td>
    );
};

// Tabla de un cierre de inventario de bodega. Se usa en la creación (con hook:
// la observación de cada fila es editable) y en el detalle de un cierre
// guardado (sin hook: todo de solo lectura, con cierreId para consultar sus
// pallets). El inventario físico nunca se digita, sale de la existencia real de
// bodega; para cambiarlo se registra un reajuste
export const CierreInventarioTable = ({
    titulo,
    campo,
    filas = [],
    hook,
    cierreId,
}) => {
    const editable = !!hook;

    const totalTeorico = totalColumna(filas, "kg_teorico");
    const totalFisico = totalColumna(filas, "kg_fisico");

    return (
        <div className="cierre-inventario__tabla">
            <h6 className="cierre-inventario__tabla-titulo">{titulo}</h6>

            <div className="new-table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>Tipo de producto</th>
                            <th>Condición</th>
                            <th>Inv. inicial</th>
                            <th>Ingresos</th>
                            <th>Reproceso</th>
                            <th>Reclasificación</th>
                            <th>Salidas</th>
                            <th>Control de calidad</th>
                            <th>Inv. teórico</th>
                            <th>Inv. físico</th>
                            <th>Diferencia</th>
                            <th>Observaciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filas.length === 0 && (
                            <tr>
                                <td colSpan={12} className="text-center">
                                    No hay productos para mostrar
                                </td>
                            </tr>
                        )}
                        {filas.map((fila, index) => {
                            const anterior = filas[index - 1];
                            const siguiente = filas[index + 1];

                            // el producto ocupa una sola celda para sus dos
                            // condiciones, igual que en el control de bodega
                            const abreProducto =
                                !anterior ||
                                anterior.producto_id !== fila.producto_id;

                            const filasDelProducto =
                                siguiente &&
                                siguiente.producto_id === fila.producto_id
                                    ? 2
                                    : 1;

                            const diferencia = diferenciaKg(fila);

                            const indiceProducto = Math.floor(index / 2);
                            const claseStripe =
                                indiceProducto % 2 === 0
                                    ? "white-row"
                                    : "gray-row";

                            return (
                                <tr
                                    key={`${fila.producto_id}-${fila.es_conforme_bodega}`}
                                    className={`cell-border-bottom ${claseStripe}`}
                                >
                                    {abreProducto && (
                                        <td
                                            className="text-nowrap cell-border-right"
                                            rowSpan={filasDelProducto}
                                        >
                                            {fila.producto_nombre}
                                        </td>
                                    )}
                                    <td className="text-nowrap">
                                        {fila.es_conforme_bodega
                                            ? "Conforme"
                                            : "No conforme"}
                                    </td>
                                    <CeldaKg valor={fila.kg_inicial} />
                                    <CeldaKg valor={fila.kg_ingresos} />
                                    <CeldaKg valor={fila.kg_reproceso} />
                                    <CeldaKg valor={fila.kg_reclasificacion} />
                                    <CeldaKg valor={fila.kg_salidas_externas} />
                                    <CeldaKg valor={fila.kg_control_calidad} />
                                    <CeldaKg valor={fila.kg_teorico} />

                                    <CeldaInventarioFisico
                                        fila={fila}
                                        index={index}
                                        campo={campo}
                                        hook={hook}
                                        cierreId={cierreId}
                                    />

                                    <CeldaKg
                                        valor={diferencia}
                                        className={
                                            diferencia !== 0
                                                ? "text-danger"
                                                : ""
                                        }
                                    />

                                    {editable ? (
                                        <td className="cierre-inventario__celda-editable">
                                            <TextField
                                                control={hook.formControl}
                                                errors={hook.formErrors}
                                                name={`${campo}.${index}.observaciones`}
                                                onBlur={(e) =>
                                                    hook.GuardarObservacion(
                                                        campo,
                                                        fila,
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </td>
                                    ) : (
                                        <td
                                            className={`${fila.observaciones ? "" : "text-center"}`}
                                        >
                                            <NullableValue
                                                value={fila.observaciones}
                                            >
                                                {fila.observaciones || ""}
                                            </NullableValue>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                    {filas.length > 0 && (
                        <tfoot>
                            <tr className="totales-row">
                                <td
                                    colSpan={2}
                                    className="text-nowrap text-center"
                                >
                                    Totales
                                </td>
                                <CeldaKg
                                    valor={totalColumna(filas, "kg_inicial")}
                                />
                                <CeldaKg
                                    valor={totalColumna(filas, "kg_ingresos")}
                                />
                                <CeldaKg
                                    valor={totalColumna(filas, "kg_reproceso")}
                                />
                                <CeldaKg
                                    valor={totalColumna(
                                        filas,
                                        "kg_reclasificacion",
                                    )}
                                />
                                <CeldaKg
                                    valor={totalColumna(
                                        filas,
                                        "kg_salidas_externas",
                                    )}
                                />
                                <CeldaKg
                                    valor={totalColumna(
                                        filas,
                                        "kg_control_calidad",
                                    )}
                                />
                                <CeldaKg valor={totalTeorico} />
                                <CeldaKg valor={totalFisico} />
                                <CeldaKg valor={totalFisico - totalTeorico} />
                                <td />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};
